(() => {
  if (window.hivisProductImageFitLoaded || !window.HivisImageBounds) return;
  window.hivisProductImageFitLoaded = true;
  const selector = '.cp-photo > img, .mp-photo > img[data-main-image], .hrp-image > img, .weekly-picks__image > img';
  const cache = new Map(), registered = new WeakSet(), visible = new WeakSet(), states = new WeakMap();
  const waiting = [], pending = new WeakSet();
  let running = 0;
  function measureSource(source) {
    if (cache.has(source)) return cache.get(source);
    const result = new Promise(resolve => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      const timer = setTimeout(() => resolve({ kind: 'unavailable' }), 8000);
      probe.onerror = () => { clearTimeout(timer); resolve({ kind: 'unavailable' }); };
      probe.onload = () => {
        clearTimeout(timer);
        try {
          const ratio = Math.min(1, 192 / probe.naturalWidth, 192 / probe.naturalHeight);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(probe.naturalWidth * ratio));
          canvas.height = Math.max(1, Math.round(probe.naturalHeight * ratio));
          const context = canvas.getContext('2d', { willReadFrequently: true });
          context.drawImage(probe, 0, 0, canvas.width, canvas.height);
          resolve(window.HivisImageBounds.measure(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height));
          canvas.width = canvas.height = 0;
        } catch { resolve({ kind: 'unavailable' }); }
      };
      probe.src = source;
    });
    cache.set(source, result);
    if (cache.size > 256) cache.delete(cache.keys().next().value);
    return result;
  }
  function reset(image) {
    if (image.dataset.imageFitScale) {
      image.style.removeProperty('transform');
      image.style.removeProperty('transform-origin');
      delete image.dataset.imageFitScale;
    }
    delete image.dataset.imageFitState;
  }
  function apply(image, source, bounds) {
    if (!image.isConnected || (image.currentSrc || image.src) !== source) return;
    const fitted = window.HivisImageBounds.fit(bounds, image.naturalWidth, image.naturalHeight, image.clientWidth, image.clientHeight);
    image.dataset.imageFitState = bounds.kind;
    if (!fitted) return;
    image.parentElement.classList.add('hivis-product-fit-frame');
    image.style.transformOrigin = 'center center';
    image.style.transform = `translate(${fitted.x.toFixed(2)}px, ${fitted.y.toFixed(2)}px) scale(${fitted.scale.toFixed(4)})`;
    image.dataset.imageFitScale = fitted.scale.toFixed(3);
  }
  async function fitImage(image) {
    if (!image.isConnected || !image.complete || !image.naturalWidth) return;
    const source = image.currentSrc || image.src;
    if (!source || getComputedStyle(image).objectFit !== 'contain') return;
    reset(image);
    const bounds = await measureSource(source);
    states.set(image, { source, bounds });
    apply(image, source, bounds);
  }
  function drain() {
    while (running < 3 && waiting.length) {
      const image = waiting.shift(); running++;
      fitImage(image).catch(() => {}).finally(() => {
        running--; pending.delete(image);
        const state = states.get(image);
        if (state && state.source !== (image.currentSrc || image.src) && image.complete) request(image);
        drain();
      });
    }
  }
  function request(image) {
    if (!visible.has(image) || pending.has(image)) return;
    pending.add(image); waiting.push(image); drain();
  }
  const intersection = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) { visible.add(entry.target); request(entry.target); }
    else visible.delete(entry.target);
  }), { rootMargin: '150px' });
  const resize = new ResizeObserver(entries => entries.forEach(entry => {
    entry.target.querySelectorAll(selector).forEach(image => {
      const state = states.get(image);
      if (state && (image.currentSrc || image.src) === state.source) {
        reset(image); apply(image, state.source, state.bounds);
      }
    });
  }));
  function register(scope) {
    const images = scope.matches?.(selector) ? [scope] : [...scope.querySelectorAll(selector)];
    images.forEach(image => {
      if (registered.has(image)) return;
      registered.add(image); reset(image);
      image.addEventListener('load', () => { reset(image); request(image); });
      intersection.observe(image); resize.observe(image.parentElement);
    });
  }
  new MutationObserver(records => records.forEach(record => {
    if (record.type === 'attributes' && record.target.matches(selector)) {
      reset(record.target); request(record.target);
    } else record.addedNodes.forEach(node => { if (node.nodeType === 1) register(node); });
  })).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  register(document);
})();

